/* assets/events-page.js */

(() => {
  "use strict";

  const SELECTORS = {
    root: "[data-events-page]",
    card: "[data-event-card]",
    list: "[data-events-list]",
    empty: "[data-events-empty]",
    dropdown: "[data-filter-dropdown]",
    toggle: "[data-filter-toggle]",
    panel: "[data-filter-panel]",
    calendar: "[data-calendar-picker]",
    dateLabel: "[data-date-label]",
    typeLabel: "[data-type-label]",
    locationLabel: "[data-location-label]",
    typeCount: "[data-type-count]",
    locationCount: "[data-location-count]",
    typeOptions: "[data-type-options]",
    locationOptions: "[data-location-options]",
    modal: "[data-event-modal]",
    modalSlot: "[data-event-modal-slot]",
    mobileToggle: "[data-mobile-filter-toggle]",
    mobileApply: "[data-mobile-filter-apply]",
    mobileCancel: "[data-mobile-filter-cancel]",
    filters: "[data-events-filters]"
  };

  const EVENTS_PER_PAGE = 8;
  const MOBILE_MAX = 749;
  const TABLET_MAX = 989;

  const pad = (value) => String(value).padStart(2, "0");
  const normalize = (value) => String(value || "").trim();
  const lower = (value) => normalize(value).toLowerCase();

  const isMobile = () => window.matchMedia(`(max-width: ${MOBILE_MAX}px)`).matches;
  const isTabletOrMobile = () => window.matchMedia(`(max-width: ${TABLET_MAX}px)`).matches;

  const escapeHtml = (value) =>
    String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const escapeSelector = (value) => {
    if (window.CSS && typeof window.CSS.escape === "function") return window.CSS.escape(value);
    return String(value || "").replace(/["\\]/g, "\\$&");
  };

  const toDateKey = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

  const parseKey = (key) => {
    if (!key) return null;
    const [year, month, day] = String(key).split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
  };

  const addMonths = (date, amount) => new Date(date.getFullYear(), date.getMonth() + amount, 1);
  const cloneSet = (set) => new Set(Array.from(set));

  const sameSet = (a, b) => {
    if (a.size !== b.size) return false;
    for (const value of a) if (!b.has(value)) return false;
    return true;
  };

  const countryFromLocation = (location) => {
    const parts = String(location || "")
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean);

    return parts.length > 1 ? parts[parts.length - 1] : normalize(location);
  };

  const countryKeyFromLocation = (location) => lower(countryFromLocation(location));

  const formatDateLabel = (key) => {
    const date = parseKey(key);
    if (!date) return "";

    if (isTabletOrMobile()) {
      return date.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric"
      });
    }

    return `${date.getDate()}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
  };

  const arrowLeft = (color = "currentColor") => `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M4.16406 10H15.8307" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4.16406 10L7.4974 13.3333" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M4.16406 10.0001L7.4974 6.66675" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

  const arrowRight = (color = "currentColor") => `
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
      <path d="M4.16406 10H15.8307" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12.5 13.3333L15.8333 10" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
      <path d="M12.5 6.66675L15.8333 10.0001" stroke="${color}" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;

 const infoIcon = () => ``;

  function createState() {
    return {
      dateStart: null,
      dateEnd: null,
      pendingStart: null,
      pendingEnd: null,
      selectedTypes: new Set(),
      pendingTypes: new Set(),
      selectedLocations: new Set(),
      pendingLocations: new Set(),
      calendarMonths: [null, null],
      page: 1
    };
  }

  document.querySelectorAll(SELECTORS.root).forEach(initEventsPage);

  function initEventsPage(root) {
    const state = createState();
    const cards = Array.from(root.querySelectorAll(SELECTORS.card));
    const list = root.querySelector(SELECTORS.list);
    const empty = root.querySelector(SELECTORS.empty);

    if (list) {
      cards
        .sort((a, b) => String(a.dataset.start || "").localeCompare(String(b.dataset.start || "")))
        .forEach((card) => {
          card.dataset.filteredVisible = "true";
          list.appendChild(card);
        });
    }

    renderOptionLists(root, cards);
    initDropdowns(root, state, cards, empty);
    initOptions(root, state, cards, empty);
    initCalendar(root, state, cards, empty);
    initPagination(root, state, cards, empty);
    initMobileFilters(root, state, cards, empty);
    initModal(root);

    updateFilterLabels(root, state, cards);
    applyFilters(root, state, cards, empty, true);
    openDeepLinkedEvent(root);

    let resizeTimer = null;

    window.addEventListener("resize", () => {
      clearTimeout(resizeTimer);

      resizeTimer = setTimeout(() => {
        updateFilterLabels(root, state, cards);
        renderCalendar(root, state);
      }, 80);
    });
  }

  function renderOptionLists(root, cards) {
    const typeContainer = root.querySelector(SELECTORS.typeOptions);
    const locationContainer = root.querySelector(SELECTORS.locationOptions);

    const typeMap = new Map();
    const locationMap = new Map();

    cards.forEach((card) => {
      const type = normalize(card.dataset.type);

      if (type && !typeMap.has(lower(type))) {
        typeMap.set(lower(type), type);
      }

      const country = countryFromLocation(card.dataset.location);
      const key = lower(country);

      if (country && !locationMap.has(key)) {
        locationMap.set(key, {
          value: key,
          label: country,
          flag: card.dataset.flagImage || ""
        });
      }
    });

    if (typeContainer) {
      const types = Array.from(typeMap.values()).sort((a, b) => a.localeCompare(b));

      typeContainer.innerHTML = types.length
        ? types
            .map((type, index) => {
              const id = `events-type-${index}`;

              return `
                <label class="events-option events-option--type" for="${id}">
                 <span class="events-badge events-badge--dark">
  <span>${escapeHtml(type)}</span>
</span>

                  <input type="checkbox" value="${escapeHtml(type)}" data-type-option id="${id}">
                  <span class="events-checkbox" aria-hidden="true"></span>
                </label>`;
            })
            .join("")
        : '<div class="events-option events-option--empty">No options</div>';
    }

    if (locationContainer) {
      const locations = Array.from(locationMap.values()).sort((a, b) => a.label.localeCompare(b.label));

      locationContainer.innerHTML = locations.length
        ? locations
            .map((location, index) => {
              const id = `events-location-${index}`;

              return `
                <label class="events-option events-option--location" for="${id}">
                  <span class="events-option__label">
                    ${
                      location.flag
                        ? `<img class="events-option__flag-img" src="${escapeHtml(location.flag)}" alt="" loading="lazy">`
                        : ""
                    }
                    <span class="events-option__location-text">${escapeHtml(location.label)}</span>
                  </span>

                  <input type="checkbox" value="${escapeHtml(location.value)}" data-location-option id="${id}">
                  <span class="events-checkbox" aria-hidden="true"></span>
                </label>`;
            })
            .join("")
        : '<div class="events-option events-option--empty">No options</div>';
    }
  }

  function initDropdowns(root, state, cards, empty) {
    root.querySelectorAll(SELECTORS.dropdown).forEach((dropdown) => {
      const toggle = dropdown.querySelector(SELECTORS.toggle);
      const panel = dropdown.querySelector(SELECTORS.panel);

      if (!toggle) return;

      toggle.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        const wasOpen = dropdown.classList.contains("is-open");

        finalizeOpenDropdowns(root, state, cards, empty);
        closeDropdowns(root);

        if (!wasOpen) {
          prepareDropdownOpen(root, dropdown, state);
          dropdown.classList.add("is-open");
          toggle.setAttribute("aria-expanded", "true");
        }
      });

      if (panel) {
        panel.addEventListener("click", (event) => {
          event.stopPropagation();
        });
      }
    });

    document.addEventListener("click", (event) => {
      const openDropdowns = Array.from(root.querySelectorAll(`${SELECTORS.dropdown}.is-open`));

      if (!openDropdowns.length) return;
      if (openDropdowns.some((dropdown) => dropdown.contains(event.target))) return;

      finalizeOpenDropdowns(root, state, cards, empty);
      closeDropdowns(root);
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;

      revertPending(root, state);
      closeDropdowns(root);
      closeMobileOverlay(root);
      updateFilterLabels(root, state, cards);
    });
  }

  function prepareDropdownOpen(root, dropdown, state) {
    if (dropdown.classList.contains("events-filter--date")) {
      state.pendingStart = state.dateStart;
      state.pendingEnd = state.dateEnd;
      renderCalendar(root, state);
    }

    if (dropdown.classList.contains("events-filter--type")) {
      state.pendingTypes = cloneSet(state.selectedTypes);
      setCheckedValues(root, "type", state.pendingTypes);
    }

    if (dropdown.classList.contains("events-filter--location")) {
      state.pendingLocations = cloneSet(state.selectedLocations);
      setCheckedValues(root, "location", state.pendingLocations);
    }
  }

  function closeDropdowns(root) {
    root.querySelectorAll(SELECTORS.dropdown).forEach((dropdown) => {
      dropdown.classList.remove("is-open");

      const toggle = dropdown.querySelector(SELECTORS.toggle);

      if (toggle) {
        toggle.setAttribute("aria-expanded", "false");
      }
    });
  }

  function finalizeOpenDropdowns(root, state, cards, empty) {
    const filters = root.querySelector(SELECTORS.filters);

    if (isMobile() && filters?.classList.contains("is-mobile-open")) return;

    let changed = false;

    root.querySelectorAll(`${SELECTORS.dropdown}.is-open`).forEach((dropdown) => {
      if (dropdown.classList.contains("events-filter--type")) {
        if (isTabletOrMobile()) {
          setCheckedValues(root, "type", state.selectedTypes);
          state.pendingTypes = cloneSet(state.selectedTypes);
          return;
        }

        const next = getCheckedValues(root, "type");
        changed = !sameSet(next, state.selectedTypes) || changed;
        state.selectedTypes = next;
        state.pendingTypes = cloneSet(next);
      }

      if (dropdown.classList.contains("events-filter--location")) {
        const next = getCheckedValues(root, "location");
        changed = !sameSet(next, state.selectedLocations) || changed;
        state.selectedLocations = next;
        state.pendingLocations = cloneSet(next);
      }
    });

    updateFilterLabels(root, state, cards);

    if (changed) {
      applyFilters(root, state, cards, empty, true);
    }
  }

  function initOptions(root, state, cards, empty) {
    root.addEventListener("change", (event) => {
      const typeInput = event.target.closest("[data-type-option]");
      const locationInput = event.target.closest("[data-location-option]");

      if (typeInput) {
        if (isTabletOrMobile()) {
          state.pendingTypes = getCheckedValues(root, "type");
        } else {
          state.selectedTypes = getCheckedValues(root, "type");
          state.pendingTypes = cloneSet(state.selectedTypes);
          updateFilterLabels(root, state, cards);
          applyFilters(root, state, cards, empty, true);
        }
      }

      if (locationInput) {
  state.selectedLocations = getCheckedValues(root, "location");
  state.pendingLocations = cloneSet(state.selectedLocations);

  updateFilterLabels(root, state, cards);
  applyFilters(root, state, cards, empty, true);
}
    });

    root.querySelectorAll('[data-apply-options="type"]').forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        state.selectedTypes = getCheckedValues(root, "type");
        state.pendingTypes = cloneSet(state.selectedTypes);

        updateFilterLabels(root, state, cards);
        applyFilters(root, state, cards, empty, true);
        closeDropdowns(root);
      });
    });

    root.querySelectorAll('[data-cancel-options="type"]').forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        state.pendingTypes = cloneSet(state.selectedTypes);
        setCheckedValues(root, "type", state.selectedTypes);
        closeDropdowns(root);
      });
    });

    root.querySelectorAll('[data-clear-options="type"]').forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        state.selectedTypes.clear();
        state.pendingTypes.clear();

        setCheckedValues(root, "type", state.selectedTypes);
        updateFilterLabels(root, state, cards);
        applyFilters(root, state, cards, empty, true);
      });
    });
  }

  function getCheckedValues(root, kind) {
    return new Set(
      Array.from(root.querySelectorAll(`input[data-${kind}-option]:checked`))
        .map((input) => normalize(input.value))
        .filter(Boolean)
    );
  }

  function setCheckedValues(root, kind, values) {
    root.querySelectorAll(`input[data-${kind}-option]`).forEach((input) => {
      input.checked = values.has(normalize(input.value));
    });
  }

  function initCalendar(root, state, cards, empty) {
    const calendar = root.querySelector(SELECTORS.calendar);

    if (!calendar) return;

    const firstDate =
      cards
        .map((card) => parseKey(card.dataset.start))
        .filter(Boolean)
        .sort((a, b) => a - b)[0] || new Date();

    const firstMonth = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);

    state.calendarMonths = [firstMonth, addMonths(firstMonth, 1)];

    renderCalendar(root, state);

    calendar.addEventListener("click", (event) => {
      const nav = event.target.closest("[data-cal-nav]");
      const day = event.target.closest("[data-cal-day]");

      if (nav) {
        event.preventDefault();
        event.stopPropagation();

        const direction = Number(nav.dataset.calNav);
        const index = Number(nav.dataset.calIndex);

        if (!Number.isNaN(index) && state.calendarMonths[index]) {
          state.calendarMonths[index] = addMonths(state.calendarMonths[index], direction);
          renderCalendar(root, state);
        }

        return;
      }

      if (day) {
        event.preventDefault();
        event.stopPropagation();

        const key = day.dataset.calDay;

        if (!state.pendingStart || state.pendingEnd) {
          state.pendingStart = key;
          state.pendingEnd = null;
        } else if (key < state.pendingStart) {
          state.pendingEnd = state.pendingStart;
          state.pendingStart = key;
        } else {
          state.pendingEnd = key;
        }

        renderCalendar(root, state);
      }
    });

    root.querySelectorAll("[data-apply-date]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        state.dateStart = state.pendingStart;
        state.dateEnd = state.pendingEnd || state.pendingStart;

        updateFilterLabels(root, state, cards);
        applyFilters(root, state, cards, empty, true);
        closeDropdowns(root);
      });
    });

    root.querySelectorAll("[data-filter-cancel]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();

        state.pendingStart = state.dateStart;
        state.pendingEnd = state.dateEnd;

        renderCalendar(root, state);
        closeDropdowns(root);
      });
    });
  }

  function renderCalendar(root, state) {
    const calendar = root.querySelector(SELECTORS.calendar);

    if (!calendar || !state.calendarMonths[0] || !state.calendarMonths[1]) return;

    calendar.innerHTML = `
      ${renderMonth(state, state.calendarMonths[0], 0)}
      ${renderMonth(state, state.calendarMonths[1], 1)}
    `;
  }

  function renderMonth(state, date, calendarIndex) {
    const year = date.getFullYear();
    const month = date.getMonth();
    const monthName = date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric"
    });

    const jsStartDay = new Date(year, month, 1).getDay();
    const mondayStartIndex = jsStartDay === 0 ? 6 : jsStartDay - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const blanks = Array.from({ length: mondayStartIndex }, () => {
      return '<span class="events-cal__day is-empty"></span>';
    }).join("");

    const days = Array.from({ length: daysInMonth }, (_, index) => {
      const day = index + 1;
      const key = toDateKey(new Date(year, month, day));
      const selected = key === state.pendingStart || key === state.pendingEnd;
      const between = state.pendingStart && state.pendingEnd && key > state.pendingStart && key < state.pendingEnd;

      return `
        <button
          type="button"
          class="events-cal__day${selected ? " is-selected" : ""}${between ? " is-between" : ""}"
          data-cal-day="${key}"
        >
          ${day}
        </button>`;
    }).join("");

    return `
      <div class="events-cal">
        <div class="events-cal__head">
          <button type="button" class="events-cal__nav" data-cal-nav="-1" data-cal-index="${calendarIndex}" aria-label="Previous month">
            ${arrowLeft()}
          </button>

          <strong>${escapeHtml(monthName)}</strong>

          <button type="button" class="events-cal__nav" data-cal-nav="1" data-cal-index="${calendarIndex}" aria-label="Next month">
            ${arrowRight()}
          </button>
        </div>

        <div class="events-cal__week">
          <span>Mon</span>
          <span>Tue</span>
          <span>Wed</span>
          <span>Thu</span>
          <span>Fri</span>
          <span>Sat</span>
          <span>Sun</span>
        </div>

        <div class="events-cal__grid">
          ${blanks}
          ${days}
        </div>
      </div>`;
  }

  function initMobileFilters(root, state, cards, empty) {
    const toggle = root.querySelector(SELECTORS.mobileToggle);
    const filters = root.querySelector(SELECTORS.filters);
    const apply = root.querySelector(SELECTORS.mobileApply);
    const cancel = root.querySelector(SELECTORS.mobileCancel);

    if (!toggle || !filters) return;

    toggle.addEventListener("click", (event) => {
      event.preventDefault();

      const open = !filters.classList.contains("is-mobile-open");

      if (open) {
        state.pendingStart = state.dateStart;
        state.pendingEnd = state.dateEnd;
        state.pendingTypes = cloneSet(state.selectedTypes);
        state.pendingLocations = cloneSet(state.selectedLocations);

        setCheckedValues(root, "type", state.pendingTypes);
        setCheckedValues(root, "location", state.pendingLocations);
        renderCalendar(root, state);

        openMobileOverlay(root);
      } else {
        revertPending(root, state);
        closeMobileOverlay(root);
      }
    });

    if (apply) {
      apply.addEventListener("click", (event) => {
        event.preventDefault();

        state.dateStart = state.pendingStart;
        state.dateEnd = state.pendingEnd || state.pendingStart;
        state.selectedTypes = getCheckedValues(root, "type");
        state.pendingTypes = cloneSet(state.selectedTypes);
        state.selectedLocations = getCheckedValues(root, "location");
        state.pendingLocations = cloneSet(state.selectedLocations);

        updateFilterLabels(root, state, cards);
        applyFilters(root, state, cards, empty, true);
        closeDropdowns(root);
        closeMobileOverlay(root);
      });
    }

    if (cancel) {
      cancel.addEventListener("click", (event) => {
        event.preventDefault();

        revertPending(root, state);
        updateFilterLabels(root, state, cards);
        closeDropdowns(root);
        closeMobileOverlay(root);
      });
    }
  }

  function openMobileOverlay(root) {
    const filters = root.querySelector(SELECTORS.filters);
    const toggle = root.querySelector(SELECTORS.mobileToggle);

    filters?.classList.add("is-mobile-open");
    root.classList.add("is-mobile-filters-open");
    document.body.classList.add("events-mobile-filters-open");
    toggle?.setAttribute("aria-expanded", "true");
  }

  function closeMobileOverlay(root) {
    const filters = root.querySelector(SELECTORS.filters);
    const toggle = root.querySelector(SELECTORS.mobileToggle);

    filters?.classList.remove("is-mobile-open");
    root.classList.remove("is-mobile-filters-open");
    document.body.classList.remove("events-mobile-filters-open");
    toggle?.setAttribute("aria-expanded", "false");
  }

  function revertPending(root, state) {
    state.pendingStart = state.dateStart;
    state.pendingEnd = state.dateEnd;
    state.pendingTypes = cloneSet(state.selectedTypes);
    state.pendingLocations = cloneSet(state.selectedLocations);

    setCheckedValues(root, "type", state.selectedTypes);
    setCheckedValues(root, "location", state.selectedLocations);
    renderCalendar(root, state);
  }

  function updateFilterLabels(root, state, cards) {
    const dateLabel = root.querySelector(SELECTORS.dateLabel);
    const typeLabel = root.querySelector(SELECTORS.typeLabel);
    const locationLabel = root.querySelector(SELECTORS.locationLabel);

    updateCount(root.querySelector(SELECTORS.typeCount), state.selectedTypes.size);
    updateCount(root.querySelector(SELECTORS.locationCount), state.selectedLocations.size);

    if (dateLabel) {
      if (state.dateStart && state.dateEnd) {
        dateLabel.textContent = `${formatDateLabel(state.dateStart)} - ${formatDateLabel(state.dateEnd)}`;
      } else if (state.dateStart) {
        dateLabel.textContent = formatDateLabel(state.dateStart);
      } else {
        dateLabel.textContent = dateLabel.dataset.defaultLabel || "Dates";
      }
    }

    if (typeLabel) {
      if (!state.selectedTypes.size) {
        typeLabel.textContent = typeLabel.dataset.defaultLabel || "Event Type";
      } else if (state.selectedTypes.size === 1) {
        typeLabel.textContent = Array.from(state.selectedTypes)[0];
      } else {
        typeLabel.textContent = typeLabel.dataset.defaultLabel || "Event Type";
      }
    }

    if (locationLabel) {
      if (!state.selectedLocations.size) {
        locationLabel.innerHTML = locationLabel.dataset.defaultLabel || "Location";
      } else if (state.selectedLocations.size === 1) {
        const key = Array.from(state.selectedLocations)[0];
        const data = getLocationData(cards, key);

        locationLabel.innerHTML = `
          <span class="events-filter__selected-location">
            ${
              data.flag
                ? `<img class="events-filter__selected-flag" src="${escapeHtml(data.flag)}" alt="" loading="lazy">`
                : ""
            }
            <span>${escapeHtml(data.label)}</span>
          </span>`;
      } else {
        locationLabel.innerHTML = locationLabel.dataset.defaultLabel || "Location";
      }
    }
  }

  function updateCount(node, count) {
    if (!node) return;

    node.hidden = count === 0;
    node.textContent = String(count);
  }

  function getLocationData(cards, countryKey) {
    const card = cards.find((item) => countryKeyFromLocation(item.dataset.location) === countryKey);

    return {
      label: card ? countryFromLocation(card.dataset.location) : countryKey,
      flag: card ? card.dataset.flagImage || "" : ""
    };
  }

  function applyFilters(root, state, cards, empty, resetPage = true) {
    let visibleCount = 0;

    cards.forEach((card) => {
      const typePass = !state.selectedTypes.size || state.selectedTypes.has(normalize(card.dataset.type));
      const locationPass = !state.selectedLocations.size || state.selectedLocations.has(countryKeyFromLocation(card.dataset.location));
      const datePass = isDatePass(state, card);
      const visible = typePass && locationPass && datePass;

      card.dataset.filteredVisible = visible ? "true" : "false";

      if (visible) {
        visibleCount += 1;
      }
    });

    if (empty) {
      empty.hidden = visibleCount !== 0;
    }

    if (resetPage) {
      state.page = 1;
    }

    applyPagination(root, state, cards);
  }

  function isDatePass(state, card) {
    if (!state.dateStart && !state.dateEnd) return true;

    const eventStart = card.dataset.start;
    const eventEnd = card.dataset.end || eventStart;
    const selectedStart = state.dateStart;
    const selectedEnd = state.dateEnd || state.dateStart;

    return eventStart <= selectedEnd && eventEnd >= selectedStart;
  }

  function initPagination(root, state, cards, empty) {
    root.addEventListener("click", (event) => {
      const pageButton = event.target.closest("[data-events-page-number]");
      const prevButton = event.target.closest("[data-events-page-prev]");
      const nextButton = event.target.closest("[data-events-page-next]");

      if (!pageButton && !prevButton && !nextButton) return;

      event.preventDefault();
      event.stopPropagation();

      const totalPages = Math.ceil(getFilteredCards(cards).length / EVENTS_PER_PAGE);

      if (totalPages <= 1) return;

      if (pageButton) {
        const page = Number(pageButton.dataset.eventsPageNumber);
        if (!Number.isNaN(page)) state.page = page;
      } else if (prevButton && state.page > 1) {
        state.page -= 1;
      } else if (nextButton && state.page < totalPages) {
        state.page += 1;
      }

      applyFilters(root, state, cards, empty, false);
    });
  }

  function getFilteredCards(cards) {
    return cards.filter((card) => card.dataset.filteredVisible !== "false");
  }

  function ensurePagination(root) {
    const list = root.querySelector(SELECTORS.list);

    if (!list) return null;

    let pagination = root.querySelector("[data-events-pagination]");

    if (!pagination) {
      pagination = document.createElement("nav");
      pagination.className = "events-pagination";
      pagination.setAttribute("data-events-pagination", "");
      pagination.setAttribute("aria-label", "Events pagination");
      list.insertAdjacentElement("afterend", pagination);
    }

    return pagination;
  }

  function applyPagination(root, state, cards) {
    const pagination = ensurePagination(root);

    if (!pagination) return;

    const filteredCards = getFilteredCards(cards);
    const totalPages = Math.ceil(filteredCards.length / EVENTS_PER_PAGE);

    cards.forEach((card) => {
      card.hidden = true;
    });

    if (!filteredCards.length) {
      pagination.hidden = true;
      pagination.innerHTML = "";
      return;
    }

    if (totalPages <= 1) {
      filteredCards.forEach((card) => {
        card.hidden = false;
      });

      pagination.hidden = true;
      pagination.innerHTML = "";
      return;
    }

    state.page = Math.min(Math.max(state.page, 1), totalPages);

    const start = (state.page - 1) * EVENTS_PER_PAGE;

    filteredCards.slice(start, start + EVENTS_PER_PAGE).forEach((card) => {
      card.hidden = false;
    });

    renderPagination(pagination, state, totalPages);
  }

  function renderPagination(pagination, state, totalPages) {
    const prevDisabled = state.page === 1;
    const nextDisabled = state.page === totalPages;

    const pages = Array.from({ length: totalPages }, (_, index) => {
      const page = index + 1;
      const active = page === state.page;

      return `
        <button
          type="button"
          class="events-pagination__item${active ? " is-active" : ""}"
          data-events-page-number="${page}"
          aria-current="${active ? "page" : "false"}"
        >
          ${page}
        </button>`;
    }).join("");

    pagination.hidden = false;

    pagination.innerHTML = `
      <button
        type="button"
        class="events-pagination__arrow"
        data-events-page-prev
        ${prevDisabled ? "disabled" : ""}
        aria-label="Previous events page"
      >
        ${arrowLeft(prevDisabled ? "#78828C" : "black")}
      </button>

      ${pages}

      <button
        type="button"
        class="events-pagination__arrow"
        data-events-page-next
        ${nextDisabled ? "disabled" : ""}
        aria-label="Next events page"
      >
        ${arrowRight(nextDisabled ? "#78828C" : "black")}
      </button>`;
  }

  function initModal(root) {
    const modal = root.querySelector(SELECTORS.modal);
    const slot = root.querySelector(SELECTORS.modalSlot);

    if (!modal || !slot) return;

    root.addEventListener("click", (event) => {
      const open = event.target.closest("[data-open-event]");
      const close = event.target.closest("[data-close-event]");
      const copy = event.target.closest("[data-copy-event-link]");
      const calendar = event.target.closest("[data-add-calendar]");

      if (open) {
        event.preventDefault();
        openEvent(root, open.dataset.openEvent);
      }

      if (close) {
        event.preventDefault();
        closeEvent(root);
      }

      if (copy) {
        event.preventDefault();
        copyEventLink(copy.dataset.eventId);
      }

      if (calendar) {
        event.preventDefault();
        addCalendar(calendar);
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && !modal.hidden) {
        closeEvent(root);
      }
    });
  }

  function openEvent(root, id, updateHash = true) {
    const template = root.querySelector(`template[data-event-template="${escapeSelector(id)}"]`);
    const modal = root.querySelector(SELECTORS.modal);
    const slot = root.querySelector(SELECTORS.modalSlot);

    if (!template || !modal || !slot) return;

    slot.innerHTML = "";
    slot.appendChild(template.content.cloneNode(true));
    modal.hidden = false;
    document.body.classList.add("events-modal-open");

    if (updateHash) {
      const url = new URL(window.location.href);
      url.hash = `event-${id}`;
      history.pushState({ eventId: id }, "", url);
    }
  }

  function closeEvent(root) {
    const modal = root.querySelector(SELECTORS.modal);
    const slot = root.querySelector(SELECTORS.modalSlot);

    if (!modal || !slot) return;

    modal.hidden = true;
    slot.innerHTML = "";
    document.body.classList.remove("events-modal-open");

    if (window.location.hash.startsWith("#event-")) {
      history.pushState("", document.title, window.location.pathname + window.location.search);
    }
  }

  function openDeepLinkedEvent(root) {
    const hash = window.location.hash || "";

    if (!hash.startsWith("#event-")) return;

    const id = decodeURIComponent(hash.replace("#event-", ""));

    setTimeout(() => {
      openEvent(root, id, false);
    }, 80);
  }

function copyEventLink(id) {
  const url = new URL(window.location.href);
  url.hash = `event-${id}`;

  const text = url.toString();

  const fallbackCopy = () => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.top = "-9999px";
    textarea.style.left = "-9999px";

    document.body.appendChild(textarea);
    textarea.select();

    try {
      document.execCommand("copy");
    } catch (error) {
      console.warn("Copy link failed:", error);
    }

    document.body.removeChild(textarea);
  };

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(text).catch(() => {
      fallbackCopy();
    });
  } else {
    fallbackCopy();
  }
}

  function addCalendar(button) {
    const title = button.dataset.title || "Event";
    const start = String(button.dataset.start || "").replaceAll("-", "");
    const endDate = parseKey(button.dataset.end || button.dataset.start);
    const location = button.dataset.location || "";

    if (endDate) {
      endDate.setDate(endDate.getDate() + 1);
    }

    const end = endDate ? toDateKey(endDate).replaceAll("-", "") : start;

    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&location=${encodeURIComponent(location)}`;

    window.open(url, "_blank", "noopener");
  }
})();